/**
 * # Authentication and token lifecycle
 *
 * `useAuthEffects` (mounted from `ContentWrapper`) coordinates Auth0, the internal JWT,
 * Redux `auth` state, cookies, and RTK Query. `useAuth` exposes sign-in/out helpers only.
 *
 * ## Two tokens
 *
 * | Token | Storage | Lifetime | Purpose |
 * |-------|---------|----------|---------|
 * | Auth0 access token | Auth0 SDK (`localStorage`, `cacheLocation`) | Auth0-controlled | Proves identity to swap for internal JWT |
 * | Internal JWT | Redux `auth.token` + `accessToken` cookie | Server: 1h (`exp` claim) | `Authorization: Bearer` on API calls |
 *
 * “Logged in” in the UI (`isAuthenticated`) means `userProfile` is loaded, not merely
 * that a JWT exists.
 *
 * ## State ownership
 *
 * - **Redux `auth.token`** — source of truth for in-flight API calls (`api.ts` reads it).
 * - **Cookie** — persistence across reloads; written on successful `swapToken` only
 *   (`authSlice` matcher). Hydrated via `tokenSetState` without re-writing the cookie.
 * - **`userProfile`** — set by `getUserInfo` success; cleared by `resetState`.
 *
 * Related modules (not imported here to avoid cycles):
 * - `services/api.ts` — 401 → `refreshAccessToken()` → retry once → `resetState` if still 401
 * - `common/tokenRefresh.ts` — single in-flight refresh (dedupes concurrent 401s)
 * - `cookies.ts` — cookie `expires` aligned to JWT `exp`
 * - `constants.ts` — `TOKEN_REFRESH_LEAD_MS` (5 min), `SWAP_TOKEN_URL_PATH`, `RESET_AUTH_ACTION_TYPE`
 *
 * ---
 *
 * ## When `swapToken` runs (Auth0 → internal JWT)
 *
 * All paths call `refreshInternalToken()` → `getAccessTokenSilently()` → `POST auth/swapToken`.
 *
 * 1. **Initial login / missing internal token** — bootstrap effect: `userProfile` absent,
 *    no `token`, `auth0IsAuthenticated` → `swapToken()`.
 * 2. **Proactive refresh** — timer fires `TOKEN_REFRESH_LEAD_MS` (5 min) before JWT `exp`;
 *    also fires immediately if `exp` is missing or already within the lead window.
 * 3. **Tab focus** — `visibilitychange` → visible tab and JWT expiring within lead window.
 * 4. **Reactive refresh (401)** — any RTK Query call (except `auth/swapToken`) gets 401 →
 *    `api.ts` calls registered `refreshAccessToken()` → same `refreshInternalToken()`.
 *
 * Successful swap updates `auth.token` and cookie via `authSlice` (`swapToken.matchFulfilled`).
 *
 * ---
 *
 * ## When Auth0 session is cleared
 *
 * Auth0 logout (`auth0Logout`) runs only when:
 *
 * - **`swapTokenError` is set** — RTK Query recorded an error on the `swapToken` mutation
 *   (bootstrap `swapToken()` path). Triggered by the dedicated effect below.
 *   Note: failures inside `refreshInternalToken()` that use `.unwrap()` still update
 *   `swapTokenError`, so a failed proactive or 401-driven refresh can also log the user out
 *   of Auth0.
 *
 * Auth0 is **not** cleared by:
 *
 * - `resetState` (internal JWT + cookie + `userProfile` cleared; Auth0 session remains).
 * - Manual `useAuth().logout()` — that calls **both** `auth0Logout()` and `resetState()`.
 *
 * After `resetState` with Auth0 still valid: bootstrap sees no `token` → `swapToken()` again.
 *
 * ---
 *
 * ## Automatic refresh: start and stop
 *
 * **Registered handler** — on mount, `registerTokenRefreshHandler(refreshInternalToken)`;
 * on unmount, `unregisterTokenRefreshHandler()` (clears in-flight promise). While
 * `ContentWrapper` is unmounted, 401 refresh returns `false` immediately.
 *
 * **Proactive timer** — started when both `token` and `auth0IsAuthenticated` are set.
 * Stopped/cleared when:
 * - effect cleanup (dependency change or unmount),
 * - `token` becomes null,
 * - `auth0IsAuthenticated` becomes false.
 * Uses `setTimeout` once per token value (not `setInterval`). New token after swap
 * reschedules ~55 min later. Failed refresh does not reschedule until deps change.
 *
 * **Visibility listener** — active while the effect is mounted; no-op when hidden,
 * no token, or Auth0 logged out.
 *
 * **401 handler** — always active while the handler is registered; skips
 * `auth/swapToken` to avoid “swap failed → 401 → swap again” on the same endpoint.
 *
 * ---
 *
 * ## Bootstrap effect (`userProfile` missing)
 *
 * ```
 * userProfile set?     → stop
 * token present?       → getUserInfo()
 * auth0 authenticated? → swapToken()
 * else                 → wait
 * ```
 *
 * Stops permanently once `getUserInfo` succeeds (`userProfile` set). Does not re-fetch
 * profile on later token-only refreshes.
 *
 * ---
 *
 * ## Loop and thundering-herd safety
 *
 * | Mechanism | Prevents |
 * |-----------|----------|
 * | `tokenRefresh.ts` `refreshInFlight` | Parallel 401s / refresh calls share one swap |
 * | `setTimeout` (not interval) for proactive refresh | Timer-driven swap loops |
 * | 401 handler excludes `SWAP_TOKEN_URL_PATH` | swap 401 → refresh → swap recursion |
 * | 401 path: at most one retry after refresh | Request ↔ 401 spin on same call |
 * | `refreshInternalToken` returns `false` if `!auth0IsAuthenticated` | swap without Auth0 |
 * | Bootstrap guard `if (userProfile) return` | swap/getUserInfo after fully logged in |
 *
 * **Residual edge cases (no infinite tight loops, but worth knowing):**
 *
 * - **Permanent `getUserInfo` 401** (misconfigured API, wrong user): refresh may succeed
 *   (new JWT) but profile call still 401 → `resetState` → bootstrap swaps again when Auth0
 *   is still valid. Repeats on each bootstrap cycle, not a tight loop.
 * - **`getUserInfo` non-401 failure** (5xx/network): `userProfile` stays null; effect may
 *   re-invoke `getUserInfo` when React/RTK identity of `getUserInfoMutation` changes.
 *   Token is not cleared automatically.
 * - **Failed proactive refresh** with Auth0 still valid: timer does not retry until
 *   `token`/`auth0IsAuthenticated` changes; user relies on 401 path or tab focus.
 * - **`swapTokenError` + `resetState`**: failed swap logs out Auth0; stale internal token
 *   may remain until a 401 `resetState` or manual logout.
 * - **Cookie without Auth0** (expired Auth0 session, cookie JWT present): refresh fails;
 *   API 401 → `resetState`; no swap until user signs in again.
 *
 * ---
 *
 * ## Typical sequences
 *
 * **Cold load, returning user:** cookie → `tokenSetState` → bootstrap `getUserInfo` →
 * if 401 → refresh (swap) → retry → profile loaded.
 *
 * **Active session ~55 min:** timer → swap → new `token` → new timer.
 *
 * **Tab backgrounded past JWT expiry:** focus → visibility refresh → swap if within lead.
 *
 * **JWT expired mid-request:** 401 → shared refresh → retry with new Bearer token.
 *
 * **Refresh impossible:** 401 → `resetState`; if Auth0 valid, bootstrap `swapToken` again;
 * if swap keeps failing, `swapTokenError` → Auth0 logout.
 */

import {useAuth0} from '@auth0/auth0-react';
import {getSignInAuthorizationParams} from '../common/authLogin';
import {
	getJwtRefreshDelayMs,
	isJwtExpiringWithin,
} from '../common/jwt';
import {
	registerTokenRefreshHandler,
	unregisterTokenRefreshHandler,
} from '../common/tokenRefresh';
import {useSwapTokenMutation, useGetUserInfoMutation} from '../services/accounts';
import {useCallback, useEffect, useRef} from 'react';
import {useAppSelector} from '../hooks';
import {
	selectAccessToken,
	selectUserProfile,
	tokenSetState,
	resetState,
	selectIsAuthenticated
} from '../containers/authSlice';
import {getAccessToken} from '../cookies';
import {useDispatch} from 'react-redux';
import {TOKEN_REFRESH_LEAD_MS} from '../constants';

type UseAuthReturnValues = any;

export const useAuthEffects = () => {
	const {isAuthenticated: auth0IsAuthenticated, getAccessTokenSilently, logout: auth0Logout} = useAuth0();
	const dispatch = useDispatch();
	const [swapTokenMutation, {error: swapTokenError}] = useSwapTokenMutation();
	const [getUserInfoMutation] = useGetUserInfoMutation();
	const userProfile = useAppSelector(selectUserProfile);
	const token = useAppSelector(selectAccessToken);
	const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const refreshInternalToken = useCallback(async (): Promise<boolean> => {
		if (!auth0IsAuthenticated) {
			return false;
		}
		try {
			const auth0Token = await getAccessTokenSilently();
			await swapTokenMutation({ auth0Token }).unwrap();
			return true;
		} catch {
			return false;
		}
	}, [auth0IsAuthenticated, getAccessTokenSilently, swapTokenMutation]);

	const swapToken = useCallback(async () => {
		await refreshInternalToken();
	}, [refreshInternalToken]);

	useEffect(() => {
		registerTokenRefreshHandler(refreshInternalToken);
		return () => unregisterTokenRefreshHandler();
	}, [refreshInternalToken]);

	/**
	 * load access token into the state from cookie
	 */
	useEffect(() => {
		const cookieToken = getAccessToken();
		if (!token && cookieToken) {
			dispatch(tokenSetState(cookieToken));
		}
	}, [token, dispatch]);

	/**
	 * clear auth0 session in case of unsuccessful token swap
	 */
	useEffect(() => {
		if (swapTokenError) {
			auth0Logout(process.env.NODE_ENV === 'development' ? { openUrl() {
				window.location.replace('localhost:3000');
			} } : undefined);
		}
	}, [swapTokenError, auth0Logout]);

	/**
	 * 1. fetch user data if it's missing and internal token is present
	 * 2. swap auth0 token for internal token in case it's missing and auth0 token present
	 */
	useEffect(() => {
		if (userProfile) {
			return;
		}
		if (token) {
			getUserInfoMutation();
		} else if (auth0IsAuthenticated) {
			swapToken();
		}
	}, [userProfile, token, auth0IsAuthenticated, getUserInfoMutation, swapToken]);

	/** Proactive refresh shortly before JWT expiry. */
	useEffect(() => {
		if (refreshTimerRef.current) {
			clearTimeout(refreshTimerRef.current);
			refreshTimerRef.current = null;
		}
		if (!token || !auth0IsAuthenticated) {
			return;
		}

		const scheduleRefresh = () => {
			const delayMs = getJwtRefreshDelayMs(token, TOKEN_REFRESH_LEAD_MS);
			if (delayMs === null || delayMs <= 0) {
				void refreshInternalToken();
				return;
			}
			refreshTimerRef.current = setTimeout(() => {
				void refreshInternalToken();
			}, delayMs);
		};

		scheduleRefresh();
		return () => {
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current);
				refreshTimerRef.current = null;
			}
		};
	}, [token, auth0IsAuthenticated, refreshInternalToken]);

	/** Refresh when returning to the tab with an expired or soon-expiring token. */
	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState !== 'visible' || !token || !auth0IsAuthenticated) {
				return;
			}
			if (isJwtExpiringWithin(token, TOKEN_REFRESH_LEAD_MS)) {
				void refreshInternalToken();
			}
		};

		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => document.removeEventListener('visibilitychange', onVisibilityChange);
	}, [token, auth0IsAuthenticated, refreshInternalToken]);

};

const useAuth = (): UseAuthReturnValues => {
	const {loginWithRedirect, isAuthenticated: auth0IsAuthenticated, getAccessTokenSilently, logout: auth0Logout, isLoading: auth0IsLoading} = useAuth0();
	const dispatch = useDispatch();
	const userProfile = useAppSelector(selectUserProfile);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const isAuthenticating = auth0IsLoading || auth0IsAuthenticated && !isAuthenticated;

	const logout = () => {
		auth0Logout();
		dispatch(resetState());
	};

	const signInWithEmail = (email: string) =>
		loginWithRedirect({
			authorizationParams: getSignInAuthorizationParams(email),
		});

	return {
		loginWithRedirect,
		signInWithEmail,
		getAccessTokenSilently,
		isAuthenticating,
		isAuthenticated,
		logout,
		userProfile,
	};
};

export default useAuth;
